import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ClipboardIcon, MessageCircleMoreIcon } from "lucide-react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionType,
  FieldEventsType,
  FieldImperativeHandle,
  IObjectWithTypename,
} from "@/components/@types";
import {
  ContextMenu,
  ContextMenuItemType,
  ContextMenuPosition,
} from "@/components/atoms/ContextMenu";
import { Field } from "@/components/organisms/Field";
import {
  useNodeField,
  useNodeSetup,
} from "@/components/organisms/Node/Node.hooks";
import { useContextFocus, useContextScope } from "@/hooks/useContextTree";
import { useFocusedWithin } from "@/hooks/useFocusedWithin";
import { State } from "@/state";
import { cn } from "@/components/ui/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The cell's own frame, as a caller draws it. What the cell's states never
 * change goes in ``style``; a ground, an overflow or a shadow - which its
 * hover and focus do change, over the caller's as Joy's ``sx`` had them - in
 * ``className``; its corner and a fixed height, which its focus reads, as
 * ``radius`` and ``height``.
 */
export interface CellSlotProps {
  ref?: (el: HTMLElement | null) => void;
  className?: string;
  style?: CSSProperties;
  /** Its corner, as CSS (its focus ring follows it). Default the theme's xs. */
  radius?: string;
  /** A fixed height, as CSS: grown past while it is multiline and focused. */
  height?: string;
  focusOutlineColor?: string;
  "data-row-index"?: number;
  "data-col-index"?: number;
  "data-grid-cell"?: boolean;
}

export interface FieldCellProps<T = unknown> {
  // Field configuration
  type: FieldDefinitionType["type"];
  getValue: FieldDefinitionType["getValue"];
  getValueOptions?: FieldDefinitionType["getValueOptions"];
  objectKey?: string;

  // Data
  data: T;

  // Editing state
  isEditable: boolean;

  // Styling
  style?: CSSProperties;
  isLoading?: boolean;

  // Field rendering options
  isMultiline?: boolean;
  /** When not null, overrides isMultiline (additively) while the cell is focused or being edited. */
  isMultilineWhenFocusedOrEditing?: boolean | null;
  /**
   * Forces the field's expanded state instead of deriving it from focus. Use
   * where the cell must show its whole value rather than one clipped line —
   * a document line's description, say, whose row grows to fit it.
   */
  isExpanded?: boolean;
  isSingleClick?: boolean;
  size?: "sm" | "md";

  // Store-based mode: provide data and objectKey, FieldCell will set up store
  // OR
  // Pre-computed mode: provide fieldEvents directly (data is still required)
  store?: any;
  fieldEvents?: FieldEventsType;
  fieldVariant?: FieldDefinitionFieldType<FieldDefinitionType>["fieldVariant"];

  onKeyDown?: (e: React.KeyboardEvent) => void;

  /** Function to get string representation of the field value for copying */
  getStringValue?: (data: T) => string;
  /** Context item info for "Add to thread" functionality */
  contextInfo?: {
    id: string;
    identifier: string;
    name: string;
    typename: string;
  };

  /** Hover background color for the cell, as CSS. Default the theme's level2. Set to null to disable hover background. */
  hoverBackgroundColor?: string | null;

  // Slot props for customizing sub-components
  slotProps?: {
    cell?: CellSlotProps;
    /** The layer holding the field: its classes and its style. */
    content?: { className?: string; style?: CSSProperties };
    /** The cell's own element's attributes - a test id, its data marks - handed on as they are. */
    container?: React.HTMLAttributes<HTMLDivElement>;
  };
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

/** A 1px ring drawn inside the cell, on its corner, in ``--field-cell-ring``. */
const CELL_RING_CLASS =
  "after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:bottom-0 after:rounded-(--field-cell-radius) after:[border:1px_solid_var(--field-cell-ring)]";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FieldCell<T>({
  type,
  getValue,
  getValueOptions,
  objectKey,
  data,
  isEditable,
  isSingleClick = false,
  style,
  isLoading = false,
  isMultiline = false,
  isMultilineWhenFocusedOrEditing = null,
  isExpanded: isExpandedOverride,
  size,
  store: providedStore,
  fieldEvents: providedFieldEvents,
  fieldVariant = "node",
  slotProps,
  hoverBackgroundColor = "var(--accent)",
  getStringValue,
  contextInfo,
}: FieldCellProps<T>) {
  // ── Slot props ─────────────────────────────────────────────────────────────
  const {
    ref: registerCellProp,
    className: cellClassName,
    style: cellStyle,
    radius: cellRadius = "0.25rem",
    height: cellHeight,
    focusOutlineColor: cellFocusOutlineColor = "var(--foreground)",
    "data-grid-cell": dataGridCell,
    ...cellDataProps
  } = slotProps?.cell ?? {};

  // ── Refs ───────────────────────────────────────────────────────────────────
  const fieldRef = useRef<FieldImperativeHandle>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // ── Focus tracking (context trees) ────────────────────────────────────────
  const focusNavOnEscape = useCallback(() => {
    navRef.current?.focus({ preventScroll: true });
  }, []);

  const {
    ref: contentRef,
    node: contentNode,
    Provider: ContentContextProvider,
  } = useContextScope({
    onEscape: focusNavOnEscape,
  });
  const { isFocusedWithin: isContentFocusedWithin, hasActiveDescendant } =
    useContextFocus(contentNode, contentRef);

  const isNavigationFocusedWithin = useFocusedWithin(navRef);

  // Keep ref-synced copies so event-handler closures always see the latest values
  const isContentFocusedWithinRef = useRef(isContentFocusedWithin);
  useLayoutEffect(() => {
    isContentFocusedWithinRef.current = isContentFocusedWithin;
  }, [isContentFocusedWithin]);

  const isNavigationFocusedWithinRef = useRef(isNavigationFocusedWithin);
  useLayoutEffect(() => {
    isNavigationFocusedWithinRef.current = isNavigationFocusedWithin;
  }, [isNavigationFocusedWithin]);

  // ── Store / field events resolution ───────────────────────────────────────
  const useStoreBasedMode = objectKey && !providedFieldEvents;

  const storeBasedStore = useStoreBasedMode
    ? useNodeSetup({ mode: "update", node: data as IObjectWithTypename }).store
    : null;

  const storeBasedField =
    useStoreBasedMode && storeBasedStore && objectKey
      ? useNodeField(storeBasedStore, objectKey)
      : null;

  const store = providedStore || storeBasedStore;

  const fieldEvents = useMemo(() => {
    return providedFieldEvents || storeBasedField?.fieldEvents
      ? { ...providedFieldEvents, ...storeBasedField?.fieldEvents }
      : undefined;
  }, [providedFieldEvents, storeBasedField?.fieldEvents]);

  // ── Data resolution ────────────────────────────────────────────────────────
  const storeBasedData =
    useStoreBasedMode && storeBasedStore
      ? storeBasedStore((state) => state.fieldValues)
      : null;

  const displayData = storeBasedData || data;

  // Derive context info from data when not explicitly provided
  const derivedContextInfo = useMemo(() => {
    if (contextInfo) return contextInfo;
    const dataObj = displayData as Record<string, unknown> | null | undefined;
    if (!dataObj) return null;
    const id = dataObj.id as string | undefined;
    const identifier = dataObj.identifier as string | undefined;
    const typename = dataObj.__typename as string | undefined;
    if (!id || !typename) return null;
    const name = (dataObj.name ||
      dataObj.description ||
      dataObj.title ||
      identifier ||
      id) as string;
    return { id, identifier: identifier || id, name, typename };
  }, [contextInfo, displayData]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const isFocusedOrEditing =
    isNavigationFocusedWithin || isContentFocusedWithin;

  // Hold node-store reconcile while this cell is focused / being edited.
  // Callbacks are read from refs so parent re-renders (new function identities)
  // do not tear down the lock mid-edit.
  const holdingInteractionLockRef = useRef(false);
  const onInteractionStartRef = useRef(fieldEvents?.onInteractionStart);
  const onInteractionEndRef = useRef(fieldEvents?.onInteractionEnd);
  onInteractionStartRef.current = fieldEvents?.onInteractionStart;
  onInteractionEndRef.current = fieldEvents?.onInteractionEnd;

  useEffect(() => {
    if (!isEditable) return;

    if (isFocusedOrEditing) {
      if (!holdingInteractionLockRef.current && onInteractionStartRef.current) {
        holdingInteractionLockRef.current = true;
        onInteractionStartRef.current();
      }
      return () => {
        // Only release on unmount while still focused; focus-loss is handled below.
        if (holdingInteractionLockRef.current) {
          holdingInteractionLockRef.current = false;
          onInteractionEndRef.current?.();
        }
      };
    }

    if (holdingInteractionLockRef.current) {
      holdingInteractionLockRef.current = false;
      onInteractionEndRef.current?.();
    }
    // Intentionally omit callback identities and focus sub-flags: only
    // isFocusedOrEditing / isEditable should acquire or release the lock.
  }, [isEditable, isFocusedOrEditing]);

  // Additively apply isMultilineWhenFocusedOrEditing while focused/editing
  const effectiveIsMultiline =
    isMultilineWhenFocusedOrEditing !== null && isFocusedOrEditing
      ? isMultiline || isMultilineWhenFocusedOrEditing
      : isMultiline;

  const shouldPassEvents =
    (isSingleClick && isEditable) || (isEditable && isFocusedOrEditing);

  // ── Style computations ─────────────────────────────────────────────────────
  // Its look at rest; under the pointer its ground; then the caller's frame;
  // then what focus draws - each over the last, as Joy's ``sx`` had them.
  const isMultilineExpanded = effectiveIsMultiline && cellHeight != null;
  const cellClassNameComputed = cn(
    "w-full h-full flex items-center relative [outline:none] [outline-offset:-1px] [transition:box-shadow_0.3s_ease-in-out] [box-shadow:inset_0_0_0_0px_transparent] z-1000 min-w-fit rounded-(--field-cell-radius)",
    type === "table" ? "overflow-visible" : "overflow-hidden",
    cellHeight != null && "h-(--field-cell-height)",
    hoverBackgroundColor != null && "hover:bg-(--field-cell-hover-bg)",
    cellClassName,
    isFocusedOrEditing && [
      "z-1001 rounded-(--field-cell-radius) overflow-visible",
      // Grown to fit while multiline, where it is otherwise held to a height.
      // Still centred: a line that fits stays where it was, and more lines
      // than fit fill the cell from its top anyway.
      isMultilineExpanded && "min-h-(--field-cell-height) h-auto bg-background",
    ],
    isContentFocusedWithin &&
      (type === "table"
        ? "[box-shadow:none]"
        : [
            "[box-shadow:inset_0_0_0_3px_color-mix(in_srgb,var(--field-cell-focus)_10%,transparent_90%)]",
            CELL_RING_CLASS,
            "[--field-cell-ring:var(--field-cell-focus)]",
          ]),
    isNavigationFocusedWithin && [
      "shadow-xs",
      !isSingleClick && "pointer-events-none!",
      CELL_RING_CLASS,
      "[--field-cell-ring:color-mix(in_srgb,var(--field-cell-outline)_25%,transparent_75%)]",
    ],
  );
  const cellStyleComputed = {
    "--field-cell-radius": cellRadius,
    ...(cellHeight != null && { "--field-cell-height": cellHeight }),
    "--field-cell-hover-bg":
      type === "table" ? "transparent" : hoverBackgroundColor,
    // A list open in it (an active descendant) rings it neutral, else primary.
    "--field-cell-focus": hasActiveDescendant
      ? "var(--muted-foreground)"
      : "var(--primary)",
    "--field-cell-outline": cellFocusOutlineColor,
    ...cellStyle,
  } as CSSProperties;

  // Url/custom keep their own click affordance even in two-click edit mode.
  const isClickThroughField = type === "url" || type === "custom";

  // ── Context menu ───────────────────────────────────────────────────────────
  const [contextMenuPosition, setContextMenuPosition] =
    useState<ContextMenuPosition | null>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuPosition(null);
  }, []);

  const contextMenuItems = useMemo((): ContextMenuItemType[] => {
    const items: ContextMenuItemType[] = [];

    items.push({
      id: "copy-value",
      label: "Copy field value",
      ReactIcon: ClipboardIcon,
      onClick: () => {
        let stringValue = "";
        if (getStringValue) {
          stringValue = getStringValue(displayData as T);
        } else {
          const value = getValue(displayData);
          if (typeof value === "string") {
            stringValue = value;
          } else if (typeof value === "number") {
            stringValue = String(value);
          } else if (value && typeof value === "object" && "name" in value) {
            stringValue = String((value as { name?: string }).name || "");
          } else {
            stringValue = String(value ?? "");
          }
        }
        navigator.clipboard.writeText(stringValue);
        State.setState({ snackbarMessage: "Copied to clipboard" });
      },
    });

    if (derivedContextInfo) {
      items.push({
        id: "add-to-thread",
        label: "Add to thread",
        ReactIcon: MessageCircleMoreIcon,
        onClick: () => {
          const {
            setActiveMessageContext,
            activeMessageContext,
            openThreadPanel,
            setIsNodePanelOpen,
            setActivePanelNodeId,
          } = State.getState();

          const alreadyExists = activeMessageContext.some(
            (item) => item.id === derivedContextInfo.id,
          );

          if (!alreadyExists) {
            setActiveMessageContext([
              ...activeMessageContext,
              {
                id: derivedContextInfo.id,
                identifier: derivedContextInfo.identifier,
                name: derivedContextInfo.name,
                typename: derivedContextInfo.typename,
              },
            ]);
            State.setState({ snackbarMessage: "Added to thread context" });
          } else {
            State.setState({ snackbarMessage: "Already in thread context" });
          }

          setActivePanelNodeId(null);
          setIsNodePanelOpen(false);
          openThreadPanel();
        },
      });
    }

    return items;
  }, [getStringValue, getValue, displayData, derivedContextInfo]);

  // ── Event handlers ─────────────────────────────────────────────────────────

  // Merges three ref targets onto the same DOM element
  const setNavRef = useCallback(
    (el: HTMLDivElement | null) => {
      navRef.current = el;
      registerCellProp?.(el);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerCellProp],
  );

  const handleNavMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditable) {
        e.stopPropagation();
        return;
      }
      if (isContentFocusedWithin) {
        e.preventDefault();
        e.stopPropagation();
        fieldRef.current?.unsetFocus();
        navRef.current?.focus({ preventScroll: true });
        return;
      }
      if (isNavigationFocusedWithin) {
        e.preventDefault();
        e.stopPropagation();
        if (fieldRef.current?.setFocus) {
          requestAnimationFrame(() => fieldRef.current?.setFocus());
        }
      }
    },
    [isEditable, isContentFocusedWithin, isNavigationFocusedWithin],
  );

  // Read-only: the nav layer lets the pointer through to the content, so a
  // link or button in it still works - and a press anywhere else on the cell
  // picks the cell, as the arrow keys would, rather than letting the browser
  // focus whatever holds the grid.
  const handleReadOnlyMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isEditable) return;
      const target = e.target as HTMLElement;
      // A portal's events bubble here through React, from outside the cell.
      if (!e.currentTarget.contains(target)) return;
      if (target.closest("a, button, [role='button']")) return;
      e.preventDefault();
      navRef.current?.focus({ preventScroll: true });
    },
    [isEditable],
  );

  const handleNavKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        if (!isEditable) return;
        if (document.activeElement !== navRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        requestAnimationFrame(() => fieldRef.current?.setFocus());
      } else if (e.key === "Escape") {
        navRef.current?.blur();
      }
    },
    [isEditable],
  );

  const handleContentMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // React portal events bubble through the React tree, not the DOM tree.
      // Ignore events whose native target lives inside a portal (outside this
      // content layer's DOM subtree) to avoid interfering with child poppers.
      if (contentRef.current && !contentRef.current.contains(e.target as Node))
        return;

      // Read-only: content stays interactive. Editing is gated by isEditable
      // on Field events, not by eating pointer events here.
      if (!isEditable) {
        return;
      }

      if (!isSingleClick) {
        // When a text input/textarea is focused (editing), allow default
        // browser behavior so text selection via click-drag works.
        if (isContentFocusedWithinRef.current) {
          const activeEl = document.activeElement;
          if (activeEl?.tagName === "INPUT" || activeEl?.tagName === "TEXTAREA")
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        if (
          isNavigationFocusedWithinRef.current &&
          fieldRef.current?.setFocus
        ) {
          requestAnimationFrame(() => fieldRef.current?.setFocus());
        }
      } else {
        requestAnimationFrame(() => {
          if (!isContentFocusedWithinRef.current) {
            fieldRef.current?.setFocus();
          }
        });
      }
    },
    [isEditable, isSingleClick],
  );

  // ── Click-away: exit editing when clicking outside the cell ──────────────
  // Focusing the nav layer (instead of just blurring the input) properly
  // transitions isContentFocusedWithin → false via the context tree.
  // Blurring to document.body alone would not work because useContextFocus
  // ignores body-focused transitions.
  useEffect(() => {
    if (!isContentFocusedWithin) return;

    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const cellEl = navRef.current?.parentElement;
      if (!cellEl || cellEl.contains(target)) return;
      if (target.closest?.("[data-floating-ui-portal]")) return;
      if (target.closest?.('[role="dialog"]')) return;

      navRef.current?.focus({ preventScroll: true });
    };

    document.addEventListener("mousedown", handleDocumentMouseDown, true);
    return () =>
      document.removeEventListener("mousedown", handleDocumentMouseDown, true);
  }, [isContentFocusedWithin]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      {...(dataGridCell != null ? { "data-grid-cell": dataGridCell } : {})}
      onContextMenu={handleContextMenu}
      onMouseDown={handleReadOnlyMouseDown}
      {...slotProps?.container}
      className={cellClassNameComputed}
      style={cellStyleComputed}
    >
      {/* Navigation layer — receives keyboard focus and routes clicks to the field */}
      <div
        {...cellDataProps}
        ref={setNavRef}
        tabIndex={0}
        onMouseDown={handleNavMouseDown}
        onKeyDown={handleNavKeyDown}
        className={cn(
          "absolute top-0 left-0 w-full h-full [outline:none]!",
          isContentFocusedWithin ? "z-999" : "z-1001",
          !isEditable || isSingleClick || isClickThroughField
            ? "pointer-events-none"
            : "pointer-events-auto",
        )}
      />

      {/* Content layer — renders the field */}
      <div
        ref={contentRef}
        onMouseDown={handleContentMouseDown}
        className={cn(
          "inline-block z-1000 w-full",
          // A disabled control - a read-only value's input - is sent no mouse
          // events at all, so a press on it reached neither the cell nor its
          // row. The pointer goes through it to the cell.
          "[&_:disabled]:pointer-events-none",
          slotProps?.content?.className,
        )}
        style={slotProps?.content?.style}
      >
        <ContentContextProvider>
          <Field
            ref={fieldRef}
            events={shouldPassEvents ? fieldEvents : undefined}
            data={displayData}
            type={type}
            getValue={getValue}
            getValueOptions={getValueOptions}
            isMultiline={effectiveIsMultiline}
            isExpanded={
              isExpandedOverride ??
              (isSingleClick
                ? isFocusedOrEditing
                : isEditable || isFocusedOrEditing)
            }
            size={size}
            store={store}
            style={style}
            isLoading={isLoading}
            fieldVariant={fieldVariant}
          />
        </ContentContextProvider>
      </div>

      <ContextMenu
        position={contextMenuPosition}
        items={contextMenuItems}
        onClose={handleCloseContextMenu}
      />
    </div>
  );
}
